// src/components/HostRoomIdModal.js
//
// Full-screen, non-dismissible modal that appears whenever the signed-in
// user is the designated host on a GT7 match that is awaiting a Room ID.
// Subscribes via collectionGroup('matches') filtered on hostUserId + awaitingRoomId.
//
// Two-step submit: type the 6-char Room ID, then visually confirm before
// it's broadcast to all racers. Submission goes through the
// submitMatchRoomId Cloud Function so writes + fan-out are server-side.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collectionGroup,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { auth, db } from '../../firebaseConfig';

const ROOM_ID_REGEX = /^[a-z0-9]{6}$/;

export default function HostRoomIdModal() {
  const [uid, setUid] = useState(auth?.currentUser?.uid || null);
  const [pendingMatches, setPendingMatches] = useState([]);
  const [step, setStep] = useState('input'); // 'input' | 'confirm' | 'submitting'
  const [roomIdInput, setRoomIdInput] = useState('');
  const [error, setError] = useState(null);

  // Track auth state — modal only listens while signed in.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);
    });
    return unsub;
  }, []);

  // Subscribe to matches where I am the host and a Room ID is awaited.
  useEffect(() => {
    if (!uid) {
      setPendingMatches([]);
      return undefined;
    }
    const q = query(
      collectionGroup(db, 'matches'),
      where('hostUserId', '==', uid),
      where('awaitingRoomId', '==', true),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => {
            // matches subcollection lives under tournaments/{eventId}/matches/{matchId}
            const segments = d.ref.path.split('/');
            const eventId = segments[1];
            return { matchId: d.id, eventId, ...d.data() };
          })
          // Defensive — only pull matches still in a state that wants a Room ID
          .filter((m) => {
            const status = String(m.status || '').toLowerCase();
            return status !== 'completed' && status !== 'cancelled';
          });
        setPendingMatches(rows);
      },
      (err) => {
        // Don't surface errors as a blocking modal — log so we can debug.
        console.warn('[HostRoomIdModal] subscription error:', err.message);
      },
    );
    return unsub;
  }, [uid]);

  // First pending match drives the modal — host completes one at a time.
  const active = pendingMatches[0] || null;

  // Reset internal state whenever the active match changes
  useEffect(() => {
    setStep('input');
    setRoomIdInput('');
    setError(null);
  }, [active?.matchId]);

  const trimmed = roomIdInput.trim().toLowerCase();
  const isValid = ROOM_ID_REGEX.test(trimmed);

  const onProceed = () => {
    if (!isValid) {
      setError('Room ID must be 6 characters (a-z, 0-9).');
      return;
    }
    setError(null);
    setStep('confirm');
  };

  const onBack = () => {
    setStep('input');
    setError(null);
  };

  const onConfirmSubmit = async () => {
    if (!active || !isValid) return;
    setStep('submitting');
    setError(null);
    try {
      const fns = getFunctions(undefined, 'us-central1');
      await httpsCallable(fns, 'submitMatchRoomId')({
        eventId: active.eventId,
        matchId: active.matchId,
        roomId: trimmed,
      });
      // Snapshot will update automatically — awaitingRoomId flips to false and
      // this match drops out of `pendingMatches`, closing the modal.
    } catch (e) {
      const raw = String(e?.message || e?.code || e);
      let friendly = raw;
      if (raw.includes('room-id-invalid')) {
        friendly = 'Room ID format invalid. Must be 6 characters (a-z, 0-9).';
      } else if (raw.includes('not-match-host')) {
        friendly = 'You are not the assigned host for this match.';
      } else if (raw.includes('match-completed') || raw.includes('match-cancelled')) {
        friendly = 'This match is no longer active.';
      }
      setError(friendly);
      setStep('confirm'); // back to confirm so they can retry
    }
  };

  if (!active) return null;

  const round = active.round || 1;

  return (
    <Modal
      visible
      animationType="fade"
      transparent={false}
      onRequestClose={() => { /* non-dismissible */ }}
    >
      <View style={styles.container}>
        <View style={styles.card}>
          <Text style={styles.heading}>Create the GT7 Lobby</Text>
          <Text style={styles.subheading}>
            You're the host for Round {round}. The race can't start until you submit the Room ID.
          </Text>

          {step === 'input' && (
            <>
              <View style={styles.steps}>
                <Text style={styles.stepText}>1. Open GT7 → Multiplayer → Create New Room</Text>
                <Text style={styles.stepText}>2. Set Room Privacy to <Text style={styles.bold}>Private</Text></Text>
                <Text style={styles.stepText}>3. Configure the race settings as specified in the event</Text>
                <Text style={styles.stepText}>4. GT7 will display a Room ID (e.g. <Text style={styles.bold}>souij4</Text>) — type it below</Text>
              </View>

              <Text style={styles.label}>Room ID</Text>
              <TextInput
                style={styles.input}
                value={roomIdInput}
                onChangeText={(t) => {
                  setRoomIdInput(t.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6));
                  if (error) setError(null);
                }}
                placeholder="e.g. souij4"
                placeholderTextColor="#777"
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={6}
              />
              {error ? <Text style={styles.error}>{error}</Text> : null}

              <TouchableOpacity
                style={[styles.button, !isValid && styles.buttonDisabled]}
                onPress={onProceed}
                disabled={!isValid}
              >
                <Text style={styles.buttonText}>Continue</Text>
              </TouchableOpacity>
            </>
          )}

          {(step === 'confirm' || step === 'submitting') && (
            <>
              <Text style={styles.label}>Double-check the Room ID</Text>
              <View style={styles.roomIdDisplay}>
                <Text style={styles.roomIdText}>{trimmed}</Text>
              </View>
              <Text style={styles.hint}>
                Compare against what GT7 is showing on your PS5. If this matches, send it to your racers.
              </Text>
              {error ? <Text style={styles.error}>{error}</Text> : null}

              <TouchableOpacity
                style={[styles.button, step === 'submitting' && styles.buttonDisabled]}
                onPress={onConfirmSubmit}
                disabled={step === 'submitting'}
              >
                {step === 'submitting' ? (
                  <ActivityIndicator color="#000" />
                ) : (
                  <Text style={styles.buttonText}>Confirm & Send to Racers</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.secondaryButton, step === 'submitting' && styles.buttonDisabled]}
                onPress={onBack}
                disabled={step === 'submitting'}
              >
                <Text style={styles.secondaryButtonText}>Re-enter</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a0d12',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#12161d',
    borderColor: '#2a2f36',
    borderWidth: 1,
    borderRadius: 16,
    padding: 24,
  },
  heading: {
    color: '#FCD000',
    fontSize: 22,
    fontWeight: '900',
    marginBottom: 8,
    textAlign: 'center',
  },
  subheading: {
    color: '#cfcfcf',
    fontSize: 14,
    marginBottom: 16,
    textAlign: 'center',
    lineHeight: 20,
  },
  steps: {
    backgroundColor: '#0f1218',
    borderColor: '#2a2f36',
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
  },
  stepText: {
    color: '#d7d7d7',
    fontSize: 13,
    marginVertical: 3,
    lineHeight: 18,
  },
  bold: {
    color: '#FCD000',
    fontWeight: '800',
  },
  label: {
    color: '#9f9f9f',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    marginBottom: 6,
    letterSpacing: 0.5,
  },
  input: {
    backgroundColor: '#0a0d12',
    borderColor: '#2a2f36',
    borderWidth: 1,
    borderRadius: 10,
    color: '#FCD000',
    fontSize: 24,
    fontWeight: '800',
    paddingVertical: 14,
    paddingHorizontal: 16,
    letterSpacing: 4,
    textAlign: 'center',
  },
  roomIdDisplay: {
    backgroundColor: '#0a0d12',
    borderColor: '#FCD000',
    borderWidth: 2,
    borderRadius: 10,
    paddingVertical: 18,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginBottom: 8,
  },
  roomIdText: {
    color: '#FCD000',
    fontSize: 36,
    fontWeight: '900',
    letterSpacing: 8,
  },
  hint: {
    color: '#9f9f9f',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 18,
  },
  error: {
    color: '#e74c3c',
    fontSize: 13,
    fontWeight: '700',
    marginTop: 8,
    marginBottom: 4,
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#FCD000',
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 16,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    color: '#000',
    fontWeight: '900',
    fontSize: 16,
    letterSpacing: 0.5,
  },
  secondaryButton: {
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  secondaryButtonText: {
    color: '#9f9f9f',
    fontWeight: '700',
    fontSize: 14,
  },
});
